const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Whether an element can actually take focus.
 *
 * offsetParent is not usable here: it is null for `position: fixed` elements
 * even when they are perfectly visible, and it is non-null for
 * `visibility: hidden` ones, which cannot be focused. getClientRects covers
 * display:none and detached nodes, so visibility is the only extra check.
 */
function isFocusable(el: HTMLElement): boolean {
  if (el.hidden || el.getClientRects().length === 0) {
    return false;
  }
  return getComputedStyle(el).visibility !== 'hidden';
}

/**
 * Focusable elements inside a container, including those nested in the shadow
 * roots of child components (the header and input controls live in their own
 * shadow trees, so a plain querySelectorAll would miss them).
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  if (!container) {
    return [];
  }

  const found: HTMLElement[] = [];
  const collect = (root: ParentNode) => {
    root.querySelectorAll<HTMLElement>('*').forEach((el) => {
      if (el.matches(FOCUSABLE_SELECTOR) && isFocusable(el)) {
        found.push(el);
      }
      if (el.shadowRoot) {
        collect(el.shadowRoot);
      }
    });
  };
  collect(container);
  return found;
}

/** The deepest active element, walking down through nested shadow roots. */
export function getActiveElement(): HTMLElement {
  let active = document.activeElement as HTMLElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement as HTMLElement;
  }
  return active;
}

/**
 * Keeps Tab and Shift+Tab cycling within the container. Call from a keydown
 * handler when the key is Tab.
 */
export function trapFocus(event: KeyboardEvent, container: HTMLElement) {
  const focusable = getFocusableElements(container);
  if (focusable.length === 0) {
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const current = getActiveElement();
  const isInside = focusable.indexOf(current) !== -1;

  if (!isInside) {
    // Focus escaped the dialog (or never entered it) - pull it back in
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && current === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && current === last) {
    event.preventDefault();
    first.focus();
  }
}
